import { type ForwardedRef, forwardRef, useEffect } from 'react';

import {
  type ListReasonForLeavingsQuery,
  type ListReasonForLeavingsQueryVariables,
  useListReasonForLeavingsQuery,
} from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListReasonForLeavingsQueryVariables, 'pageNumber' | 'pageSize'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListReasonForLeavingsQuery['listReasonForLeavings']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListReasonForLeavingsQueryVariables, Data> => {
  const { data, ...rest } = useListReasonForLeavingsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      enabled: variables?.enabled ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listReasonForLeavings?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
      })) || [],
    ...rest,
  };
};

export const ReasonForLeavingSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const ReasonForLeavingSelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
  return (
    <SelectMultiple multipleSelectedLabel={length => `${length} reasons`} {...props} data={data} loading={loading} />
  );
};
